'use client';

/**
 * Main Checkout Hook (Coordinator)
 *
 * Per CHECKOUT_ARCHITECTURE.md Section 2 Decision 3: Composed Hooks
 *
 * This coordinator hook composes the focused sub-hooks:
 * - useCheckoutLifecycle: Session management (create, restore, refresh, clear)
 * - useCheckoutCart: Cart operations (addLine, removeLine, updateQuantity)
 * - useCheckoutAddress: Address operations (updateEmail, updateShippingAddress, updateBillingAddress)
 * - useCheckoutDelivery: Delivery method selection
 * - useCheckoutPayment: Payment state management
 *
 * Plus coordinator-only methods:
 * - braceletSizeUpdate: Product-specific metadata update
 * - goToCheckout: Navigation helper
 *
 * @see lib/checkout/hooks/ for individual sub-hooks
 */

import { useRouter } from '@/lib/i18n/navigation';
import { useParams } from 'next/navigation';
import { useCallback } from 'react';
import { useSnapshot } from 'valtio';

import {
  checkoutLineBraceletSizeUpdate,
  giftMetadataDelete,
  giftMetadataUpdate,
} from '@/lib/checkout/operations';
import { checkoutActions, checkoutState } from '@/states/checkout';

import { useCheckoutAddress } from '@/lib/checkout/hooks/useCheckoutAddress';
import { useCheckoutCart } from '@/lib/checkout/hooks/useCheckoutCart';
import { useCheckoutDelivery } from '@/lib/checkout/hooks/useCheckoutDelivery';
import { useCheckoutLifecycle } from '@/lib/checkout/hooks/useCheckoutLifecycle';
import { useCheckoutPayment } from '@/lib/checkout/hooks/useCheckoutPayment';

/**
 * Main checkout hook composing all sub-hooks
 *
 * @example
 * ```tsx
 * function CartButton() {
 *   const { linesCount, isEmpty, addLine, removeLine } = useCheckout();
 *
 *   return (
 *     <button onClick={() => addLine({ variantId: '123', quantity: 1 })}>
 *       Add to Cart ({linesCount})
 *     </button>
 *   );
 * }
 * ```
 */
export function useCheckout() {
  const params = useParams();
  const router = useRouter();

  // Parse locale from params
  const locale = (params?.lang as string) || 'us-en';
  if (checkoutState.locale !== locale) {
    checkoutActions.setLocale(locale);
  }

  // Compose sub-hooks
  const lifecycle = useCheckoutLifecycle({ locale });
  const cart = useCheckoutCart({ locale });
  const address = useCheckoutAddress({ locale });
  const delivery = useCheckoutDelivery({ locale });
  const payment = useCheckoutPayment({ locale });

  // Additional state from snapshot (for computed values not in sub-hooks)
  const snapshot = useSnapshot(checkoutState);

  /**
   * Update bracelet/wrist size for a metal bracelet watch
   * (Product-specific, not in sub-hooks)
   */
  const braceletSizeUpdate = useCallback(
    async (lineId: string, braceletSize: string) => {
      checkoutActions.setProcessing(true);
      try {
        const { errors } = await checkoutLineBraceletSizeUpdate({
          lineId,
          braceletSize,
        });

        if (errors?.length) {
          checkoutActions.setErrors(errors, 'checkout');
          return errors;
        }

        // Refresh checkout to get updated metadata
        await lifecycle.refresh();
        return [];
      } finally {
        checkoutActions.setProcessing(false);
      }
    },
    [lifecycle]
  );

  /**
   * Save gift metadata (from + message) on the checkout
   */
  const giftUpdate = useCallback(async (giftFrom: string, giftMessage: string) => {
    if (!checkoutState.id) {
      return { success: false, errors: [{ code: 'NO_CHECKOUT', message: 'No checkout found' }] };
    }
    checkoutActions.setProcessing(true);
    try {
      const { errors } = await giftMetadataUpdate({
        id: checkoutState.id,
        giftFrom,
        giftMessage,
      });
      if (errors?.length) {
        checkoutActions.setErrors(errors, 'checkout');
        return { success: false, errors };
      }
      if (checkoutState.checkout) {
        checkoutState.checkout.isGift = true;
        checkoutState.checkout.giftFrom = giftFrom;
        checkoutState.checkout.giftMessage = giftMessage;
      }
      return { success: true, errors: [] };
    } finally {
      checkoutActions.setProcessing(false);
    }
  }, []);

  /**
   * Remove gift metadata from the checkout
   */
  const giftDelete = useCallback(async () => {
    if (!checkoutState.id) {
      return { success: false, errors: [{ code: 'NO_CHECKOUT', message: 'No checkout found' }] };
    }
    checkoutActions.setProcessing(true);
    try {
      const { errors } = await giftMetadataDelete({ id: checkoutState.id });
      if (errors?.length) {
        checkoutActions.setErrors(errors, 'checkout');
        return { success: false, errors };
      }
      if (checkoutState.checkout) {
        checkoutState.checkout.isGift = false;
        checkoutState.checkout.giftFrom = null;
        checkoutState.checkout.giftMessage = null;
      }
      return { success: true, errors: [] };
    } finally {
      checkoutActions.setProcessing(false);
    }
  }, []);

  /**
   * Navigate to checkout
   */
  const goToCheckout = useCallback(() => {
    router.push(`/store/checkout/delivery`);
  }, [router]);

  return {
    // Lifecycle (create, restore, refresh, clear)
    ...lifecycle,

    // Cart (lines, addLine, removeLine, updateQuantity, subtotal, currency)
    ...cart,

    // Address (email, shippingAddress, billingAddress, updateEmail, updateShippingAddress, updateBillingAddress)
    ...address,

    // Delivery (availableMethods, selectedMethod, updateDeliveryMode)
    ...delivery,

    // Payment (status, selectMethod as selectPaymentMethod, etc.)
    ...payment,

    // Additional computed state not in sub-hooks
    isIncludeWatch: snapshot.isIncludeWatch,
    isReadyForPayment: snapshot.isReadyForPayment,
    isGuest: snapshot.isGuest,
    voucherCode: snapshot.voucherCode,
    gwpProductId: snapshot.gwpProductId,
    notification: snapshot.notification,
    orderId: snapshot.orderId,
    apiErrors: snapshot.apiErrors,
    preOrderData: snapshot.preOrderData,

    // Coordinator-only methods
    braceletSizeUpdate,
    giftUpdate,
    giftDelete,
    goToCheckout,

    // Legacy action access (for migration)
    actions: checkoutActions,
  };
}

/**
 * Lightweight hook for just cart count
 */
export function useCartCount(): number {
  const state = useSnapshot(checkoutState);
  return state.linesCount;
}

/**
 * Hook to check if cart is empty
 */
export function useIsCartEmpty(): boolean {
  const state = useSnapshot(checkoutState);
  return state.isEmpty;
}

/**
 * Hook to check if checkout is processing
 */
export function useIsCheckoutProcessing(): boolean {
  const state = useSnapshot(checkoutState);
  return state.isProcessing;
}

/**
 * Hook to check if checkout is busy (processing a mutation or syncing with server).
 * Use this to disable "Add to Bag" buttons while the cart is being updated.
 */
export function useIsCheckoutBusy(): boolean {
  const state = useSnapshot(checkoutState);
  return state.isProcessing || state.isSyncing;
}

/**
 * Hook to check if checkout is ready for payment
 */
export function useIsReadyForPayment(): boolean {
  const state = useSnapshot(checkoutState);
  return state.isReadyForPayment;
}
